import * as React from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Settings } from '../../controllers';
import { TokenValidator } from '../../utils';
import PtsOffsetViewer from './ptsOffsetViewer';

// PTS Offset: X/Y/Z (mm) the controller adds to every .pts point before the file goes to Digital Dental.
// One value for every part. 0.0 / 0.0 / 0.0 = files are sent unchanged.
const LIMIT = 5;
const ADMIN_ROLE = 4;
// Up to three decimals (numeric(4,3) in the database), e.g. 3.245
const OFFSET_TEXT = /^[+-]?\d{1,2}(\.\d{1,3})?$/;
const correlationId = () => window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : ((r & 3) | 8)).toString(16);
    });
const displayTime = value => value ? new Date(value).toLocaleString() : '—';
// The decimals the value uses, at least one: 0 -> 0.0, 0.7 -> 0.7, 3.24 -> 3.24, 3.245 -> 3.245
const fixed = value => Number(value || 0).toFixed(3).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '.0');
const signed = value => `${Number(value) > 0 ? '+' : ''}${fixed(value)}`;
const offsetError = text => {
    const value = String(text).trim();
    if (!OFFSET_TEXT.test(value)) return 'Use a number with up to three decimals, e.g. 3.245 or -2.3';
    if (Math.abs(Number(value)) > LIMIT) return `Must be between -${LIMIT}.0 and ${LIMIT}.0`;
    return '';
};

export default function PtsOffset() {
    const [current, setCurrent] = React.useState(null);
    const [form, setForm] = React.useState({x: '0.0', y: '0.0', z: '0.0', reason: ''});
    const [history, setHistory] = React.useState([]);
    const [historyNote, setHistoryNote] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [busy, setBusy] = React.useState(false);
    const [confirm, setConfirm] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [isAdmin, setIsAdmin] = React.useState(false);

    const token = () => TokenValidator.GetInternalUser();
    const load = React.useCallback(async () => {
        setLoading(true);
        // Only admins can change it (the backend enforces it too); everybody else sees the value read-only.
        try {
            const payload = await TokenValidator.SimplifyToken(await token());
            const roles = Number(payload.roles !== undefined ? payload.roles : payload.user_data && payload.user_data.roles);
            setIsAdmin((roles & ADMIN_ROLE) === ADMIN_ROLE);
        } catch (_) { setIsAdmin(false); }
        const response = await Settings.GetPtsOffset(await token());
        if (!response || !response.success) {
            setLoading(false);
            setMessage({severity: 'error', text: response ? response.message : 'Unable to load the PTS offset'});
            return;
        }
        setCurrent(response.result);
        setForm({x: fixed(response.result.offsetX), y: fixed(response.result.offsetY),
            z: fixed(response.result.offsetZ), reason: ''});
        const past = await Settings.PtsOffsetHistory(await token());
        if (past && past.success) { setHistory(past.result); setHistoryNote(''); }
        else { setHistory([]); setHistoryNote(past ? past.message : 'History unavailable'); }
        setLoading(false);
    }, []);
    React.useEffect(() => { load(); }, [load]);

    const xError = offsetError(form.x);
    const yError = offsetError(form.y);
    const zError = offsetError(form.z);
    const changed = current && !xError && !yError && !zError
        && (Number(form.x) !== Number(current.offsetX) || Number(form.y) !== Number(current.offsetY)
            || Number(form.z) !== Number(current.offsetZ));
    const canSave = changed && form.reason.trim().length >= 3 && !busy;

    const save = async () => {
        setBusy(true);
        const response = await Settings.UpdatePtsOffset(await token(), {
            offsetX: form.x.trim(), offsetY: form.y.trim(), offsetZ: form.z.trim(), reason: form.reason.trim(), correlationId: correlationId()
        });
        setBusy(false);
        setConfirm(false);
        if (!response || !response.success) {
            setMessage({severity: 'error', text: response ? response.message : 'Unable to save the PTS offset'});
            return;
        }
        const saved = response.result || {};
        setMessage({severity: 'success', text: [saved.offsetX, saved.offsetY, saved.offsetZ].every(v => Number.isFinite(Number(v)))
            ? `PTS offset saved: X ${signed(saved.offsetX)} mm, Y ${signed(saved.offsetY)} mm, Z ${signed(saved.offsetZ)} mm. It applies to the next parts sent to Digital Dental.`
            : 'PTS offset saved. It applies to the next parts sent to Digital Dental.'});
        await load();
    };

    const nonZero = !xError && !yError && !zError && (Number(form.x) !== 0 || Number(form.y) !== 0 || Number(form.z) !== 0);
    // The preview follows what an admin is typing, axis by axis (an axis being edited and not valid yet keeps the
    // saved value); everybody else sees the saved value.
    const savedX = current ? Number(current.offsetX) : 0;
    const savedY = current ? Number(current.offsetY) : 0;
    const savedZ = current ? Number(current.offsetZ) : 0;
    const preview = {
        x: isAdmin && !xError ? Number(form.x) : savedX,
        y: isAdmin && !yError ? Number(form.y) : savedY,
        z: isAdmin && !zError ? Number(form.z) : savedZ,
    };

    return <Box sx={{padding: 2}}>
        <Typography variant='h5' sx={{mb: 1}}>PTS Offset</Typography>
        <Typography variant='body2' sx={{mb: 2, color: 'gray'}}>
            Before each .pts file is sent to Digital Dental, the controller adds these values (mm) to the X, Y and Z of every point.
            The original file in the order folder is never modified. With 0.0 / 0.0 / 0.0 the file is sent exactly as it is.
        </Typography>
        {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{mb: 2}}>{message.text}</Alert>}
        {loading && <Box sx={{display: 'flex', justifyContent: 'center', padding: 4}}><CircularProgress /></Box>}

        {!loading && current && <Paper variant='outlined' sx={{p: 2, mb: 2}}>
            <Typography variant='subtitle2' sx={{color: 'gray'}}>Current offset</Typography>
            <Typography variant='h4' sx={{my: 1}}>
                X {signed(current.offsetX)} mm &nbsp;·&nbsp; Y {signed(current.offsetY)} mm &nbsp;·&nbsp; Z {signed(current.offsetZ)} mm
            </Typography>
            <Typography variant='body2' sx={{color: 'gray'}}>
                Last change: {displayTime(current.updatedAt)}{history.length && history[0].actor ? ` by ${history[0].actor}` : ''}
            </Typography>
        </Paper>}

        {!loading && current && !isAdmin && <Alert severity='info' sx={{mb: 2}}>
            Only administrators can change the PTS offset.
        </Alert>}

        {!loading && current && isAdmin && <Paper variant='outlined' sx={{p: 2, mb: 2}}>
            <Typography variant='subtitle2' sx={{mb: 2}}>New offset (up to three decimals, from -{LIMIT}.0 to {LIMIT}.0 mm)</Typography>
            <Stack direction='row' gap={2} sx={{mb: 2}} flexWrap='wrap'>
                <TextField label='X (mm)' size='small' value={form.x} error={!!xError} helperText={xError || ' '}
                    inputProps={{inputMode: 'decimal'}} onChange={e => setForm({...form, x: e.target.value})} disabled={busy} />
                <TextField label='Y (mm)' size='small' value={form.y} error={!!yError} helperText={yError || ' '}
                    inputProps={{inputMode: 'decimal'}} onChange={e => setForm({...form, y: e.target.value})} disabled={busy} />
                <TextField label='Z (mm)' size='small' value={form.z} error={!!zError} helperText={zError || ' '}
                    inputProps={{inputMode: 'decimal'}} onChange={e => setForm({...form, z: e.target.value})} disabled={busy} />
                <Button startIcon={<RestartAltIcon />} onClick={() => setForm({...form, x: '0.0', y: '0.0', z: '0.0'})} disabled={busy}>Set to 0.0 / 0.0 / 0.0</Button>
            </Stack>
            {!xError && !yError && !zError && <Typography variant='body2' sx={{mb: 2}}>
                Example: a point (10.0000, 20.0000, 5.0000) will be sent as ({(10 + Number(form.x)).toFixed(4)}, {(20 + Number(form.y)).toFixed(4)}, {(5 + Number(form.z)).toFixed(4)}).
            </Typography>}
            {nonZero && <Alert severity='warning' sx={{mb: 2}}>
                A non-zero offset moves the trim line of every G-code generated from now on. Use it only with Quality approval.
            </Alert>}
            <TextField label='Reason (required)' size='small' fullWidth multiline minRows={2} value={form.reason}
                onChange={e => setForm({...form, reason: e.target.value})} disabled={busy} sx={{mb: 2}} />
            <Stack direction='row' gap={1} justifyContent='flex-end'>
                <Button startIcon={<RefreshIcon />} onClick={load} disabled={busy}>Reload</Button>
                <Button variant='contained' startIcon={<SaveIcon />} onClick={() => setConfirm(true)} disabled={!canSave}>Save</Button>
            </Stack>
        </Paper>}

        {!loading && current && <Paper variant='outlined' sx={{p: 2, mb: 2}}>
            <Typography variant='subtitle2' sx={{mb: 1}}>Preview on a sample part</Typography>
            <PtsOffsetViewer offsetX={preview.x} offsetY={preview.y} offsetZ={preview.z}
                pending={preview.x !== savedX || preview.y !== savedY || preview.z !== savedZ} />
        </Paper>}

        {!loading && <Paper variant='outlined' sx={{p: 2}}>
            <Typography variant='subtitle2' sx={{mb: 1}}>History</Typography>
            {historyNote && <Alert severity='info' sx={{mb: 1}}>{historyNote}</Alert>}
            <TableContainer><Table size='small'>
                <TableHead><TableRow>
                    <TableCell>Date</TableCell><TableCell>User</TableCell><TableCell>Before (X / Y / Z)</TableCell>
                    <TableCell>After (X / Y / Z)</TableCell><TableCell>Reason</TableCell>
                </TableRow></TableHead>
                <TableBody>
                    {history.map(row => <TableRow key={row.correlation_id}>
                        <TableCell>{displayTime(row.created_at)}</TableCell>
                        <TableCell>{row.actor || '—'}</TableCell>
                        <TableCell>{signed(row.before_x)} / {signed(row.before_y)} / {signed(row.before_z)}</TableCell>
                        <TableCell>{signed(row.after_x)} / {signed(row.after_y)} / {signed(row.after_z)}</TableCell>
                        <TableCell>{row.reason}</TableCell>
                    </TableRow>)}
                    {!history.length && !historyNote && <TableRow><TableCell colSpan={5} align='center'>No changes yet.</TableCell></TableRow>}
                </TableBody>
            </Table></TableContainer>
        </Paper>}

        <Dialog open={confirm} onClose={() => !busy && setConfirm(false)} maxWidth='xs' fullWidth>
            <DialogTitle>Confirm PTS offset</DialogTitle>
            <DialogContent>
                {current && <Typography>
                    X {signed(current.offsetX)} → {signed(form.x)} mm<br />Y {signed(current.offsetY)} → {signed(form.y)} mm
                    <br />Z {signed(current.offsetZ)} → {signed(form.z)} mm
                </Typography>}
                <Typography variant='body2' sx={{mt: 1, color: 'gray'}}>
                    Applies to the parts sent to Digital Dental after saving, including G-code restarts. Parts already sent are not changed.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setConfirm(false)} disabled={busy}>Cancel</Button>
                <Button variant='contained' onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Confirm'}</Button>
            </DialogActions>
        </Dialog>
    </Box>;
}
